export type Point = [number, number];

export type SegmentCoordinate = [number, number];

export type RectGeometry = [number, number, number, number];

export type NearestSegment = {
  segment: Segment;
  sourceIndex: number;
  distance: number;
  t0: number;
  t1: number;
};

export type ClippedSegment = {
  segment: Segment;
  sourceIndex: number;
  t0: number;
  t1: number;
};

// Математика сегмента повторяет алгоритм из ScreencastTooltips.md.
export class Segment {
  x1: number;

  y1: number;

  x2: number;

  y2: number;

  len: number;

  cos: number;

  sin: number;

  constructor(x1: number, y1: number, x2: number, y2: number) {
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;

    const dx = x2 - x1;
    const dy = y2 - y1;

    this.len = Math.sqrt(dx * dx + dy * dy);
    if (this.len > 0) {
      this.cos = dx / this.len;
      this.sin = dy / this.len;
    } else {
      this.cos = 0;
      this.sin = 0;
    }
  }

  // Преобразует точку (x, y) в систему координат сегмента.
  toSegmentCoordinateSystem(x: number, y: number): SegmentCoordinate {
    const dx = x - this.x1;
    const dy = y - this.y1;

    return [dx * this.cos + dy * this.sin, dy * this.cos - dx * this.sin];
  }

  // Вычисляет расстояние от точки (x, y) до сегмента.
  getDistance(x: number, y: number): number {
    if (this.len > 0) {
      const [xs, ys] = this.toSegmentCoordinateSystem(x, y);

      if (xs < 0) {
        return this.getSimpleDistance(this.x1, this.y1, x, y);
      }

      if (xs > this.len) {
        return this.getSimpleDistance(this.x2, this.y2, x, y);
      }

      return Math.abs(ys);
    }

    return this.getSimpleDistance(this.x1, this.y1, x, y);
  }

  // Упрощенное расстояние между двумя точками.
  getSimpleDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.abs(x1 - x2) + Math.abs(y1 - y2);
  }

  // Интерполирует значение для точки (x, y) на этом сегменте.
  // v1 — значение в начале сегмента, v2 — в конце.
  // Проекция ограничивается диапазоном [0, len], чтобы исключить экстраполяцию за границы сегмента.
  getValue(x: number, y: number, v1: number, v2: number): number {
    if (this.len > 0) {
      const [xs] = this.toSegmentCoordinateSystem(x, y);
      const clampedXs = Math.max(0, Math.min(xs, this.len));
      return v1 + ((v2 - v1) * clampedXs) / this.len;
    }

    return v1;
  }
}

export function buildSegments(points: Point[]): Segment[] {
  if (points.length < 2) {
    return [];
  }

  const segments: Segment[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    segments.push(new Segment(x1, y1, x2, y2));
  }

  return segments;
}

export function intersectRects(firstRect: RectGeometry, secondRect: RectGeometry): RectGeometry | null {
  const [firstXmin, firstYmin, firstXmax, firstYmax] = firstRect;
  const [secondXmin, secondYmin, secondXmax, secondYmax] = secondRect;

  const xmin = Math.max(firstXmin, secondXmin);
  const ymin = Math.max(firstYmin, secondYmin);
  const xmax = Math.min(firstXmax, secondXmax);
  const ymax = Math.min(firstYmax, secondYmax);

  if (xmin > xmax || ymin > ymax) {
    return null;
  }

  return [xmin, ymin, xmax, ymax];
}

// Алгоритм Liang-Barsky описывает отрезок параметром t в диапазоне [0, 1]:
// t = 0 соответствует началу исходного сегмента, t = 1 — концу.
// Во время клиппинга t0/t1 сужают видимую часть сегмента внутри прямоугольника.
export function clipSegmentToRect(segment: Segment, sourceIndex: number, rect: RectGeometry): ClippedSegment | null {
  const [xmin, ymin, xmax, ymax] = rect;
  const dx = segment.x2 - segment.x1;
  const dy = segment.y2 - segment.y1;

  let t0 = 0;
  let t1 = 1;

  // Проверяет пересечение параметрического отрезка с одной границей прямоугольника.
  // p задает направление относительно границы, q — расстояние до нее.
  const clip = (p: number, q: number): boolean => {
    // Отрезок параллелен границе: он подходит только если лежит с внутренней стороны.
    if (p === 0) {
      return q >= 0;
    }

    const ratio = q / p;

    // Потенциальная входная граница: двигаем начало видимого интервала.
    if (p < 0) {
      if (ratio > t1) {
        return false;
      }

      if (ratio > t0) {
        t0 = ratio;
      }

      return true;
    }

    // Потенциальная выходная граница: двигаем конец видимого интервала.
    if (ratio < t0) {
      return false;
    }

    if (ratio < t1) {
      t1 = ratio;
    }

    return true;
  };

  if (
    !clip(-dx, segment.x1 - xmin) ||
    !clip(dx, xmax - segment.x1) ||
    !clip(-dy, segment.y1 - ymin) ||
    !clip(dy, ymax - segment.y1)
  ) {
    return null;
  }

  const clippedX1 = segment.x1 + dx * t0;
  const clippedY1 = segment.y1 + dy * t0;
  const clippedX2 = segment.x1 + dx * t1;
  const clippedY2 = segment.y1 + dy * t1;

  return {
    segment: new Segment(clippedX1, clippedY1, clippedX2, clippedY2),
    sourceIndex,
    t0,
    t1,
  };
}

// Ищет ближайший к курсору сегмент. Если передан clipRect, каждый исходный сегмент
// сначала обрезается по родительской области, но sourceIndex сохраняет связь с исходными values.
export function getNearestSegment(
  segments: Segment[],
  x: number,
  y: number,
  clipRect?: RectGeometry | null,
): NearestSegment | null {
  let nearest: NearestSegment | null = null;

  for (let i = 0; i < segments.length; i += 1) {
    const candidate = clipRect
      ? clipSegmentToRect(segments[i], i, clipRect)
      : {
          segment: segments[i],
          sourceIndex: i,
          t0: 0,
          t1: 1,
        };

    if (!candidate) {
      continue;
    }

    const distance = candidate.segment.getDistance(x, y);

    if (!nearest || distance < nearest.distance) {
      nearest = {
        segment: candidate.segment,
        sourceIndex: candidate.sourceIndex,
        distance,
        t0: candidate.t0,
        t1: candidate.t1,
      };
    }
  }

  return nearest;
}

// Интерполирует значение на ближайшем сегменте. Для обычного сегмента t0/t1 равны 0/1,
// для обрезанного сегмента они сдвигают значения к фактически видимой части линии.
export function interpolateOnNearestSegment(
  segments: Segment[],
  values: number[],
  x: number,
  y: number,
  clipRect?: RectGeometry | null,
): number | null {
  if (segments.length === 0 || values.length < 2) {
    return null;
  }

  const nearest = getNearestSegment(segments, x, y, clipRect);
  if (!nearest) {
    return null;
  }

  const v1 = values[nearest.sourceIndex];
  const v2 = values[nearest.sourceIndex + 1];

  if (!Number.isFinite(v1) || !Number.isFinite(v2)) {
    return null;
  }

  const clippedV1 = v1 + (v2 - v1) * nearest.t0;
  const clippedV2 = v1 + (v2 - v1) * nearest.t1;

  return nearest.segment.getValue(x, y, clippedV1, clippedV2);
}
